var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { $auth } from 'ziphy-web-shared/basic/entities/auth';
import { showAlert } from 'ziphy-web-shared/basic/lib/utilities';
class LivequeueConnection {
    constructor(url) {
        this.socket = null;
        this.reconnectTimer = null;
        this.connecting = false;
        this.connected = false;
        this.subscribers = new Set();
        this.socketListeners = new Set();
        this.url = url;
    }
    static get(url) {
        let instance = LivequeueConnection.instances.get(url);
        if (!instance) {
            instance = new LivequeueConnection(url);
            LivequeueConnection.instances.set(url, instance);
        }
        return instance;
    }
    addSubscriber(sub) {
        var _a;
        this.subscribers.add(sub);
        this.connect();
        (_a = sub.setConnected) === null || _a === void 0 ? void 0 : _a.call(sub, this.connected);
    }
    removeSubscriber(sub) {
        this.subscribers.delete(sub);
        if (this.subscribers.size === 0) {
            if (this.reconnectTimer) {
                clearTimeout(this.reconnectTimer);
                this.reconnectTimer = null;
            }
            if (this.socket) {
                try {
                    ;
                    this.socket.onmessage = null;
                    this.socket.onerror = null;
                    this.socket.onclose = null;
                    if (this.socket.readyState === WebSocket.OPEN) {
                        this.socket.close();
                    }
                    else if (this.socket.readyState === WebSocket.CONNECTING) {
                        this.socket.onopen = () => {
                            var _a;
                            try {
                                (_a = this.socket) === null || _a === void 0 ? void 0 : _a.close();
                            }
                            catch (_b) { }
                        };
                    }
                }
                catch (_a) { }
                this.socket = null;
                this.emitSocketChange(null);
            }
            this.connected = false;
        }
    }
    connect() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.connecting || this.socket)
                return;
            this.connecting = true;
            try {
                const token = yield $auth.getAccessToken();
                const ws = new WebSocket(this.url, token);
                this.socket = ws;
                this.emitSocketChange(ws);
                ws.onopen = () => {
                    this.connecting = false;
                    this.connected = true;
                    this.subscribers.forEach((s) => { var _a; return (_a = s.setConnected) === null || _a === void 0 ? void 0 : _a.call(s, true); });
                    this.subscribers.forEach((s) => { var _a; return (_a = s.onOpen) === null || _a === void 0 ? void 0 : _a.call(s); });
                };
                ws.onmessage = (event) => {
                    try {
                        const msg = JSON.parse(event.data);
                        this.subscribers.forEach((s) => { var _a; return (_a = s.onMessage) === null || _a === void 0 ? void 0 : _a.call(s, msg); });
                        if (msg.action === 'auth.expiring') {
                            $auth.getAccessToken().then((newToken) => {
                                this.send({ action: 'auth.update', payload: { token: newToken } });
                            });
                        }
                    }
                    catch (err) {
                        this.subscribers.forEach((s) => { var _a; return (_a = s.onError) === null || _a === void 0 ? void 0 : _a.call(s, err); });
                    }
                };
                ws.onerror = (err) => {
                    this.subscribers.forEach((s) => { var _a; return (_a = s.onError) === null || _a === void 0 ? void 0 : _a.call(s, err); });
                };
                ws.onclose = (ev) => {
                    this.connected = false;
                    this.socket = null;
                    this.connecting = false;
                    this.emitSocketChange(null);
                    this.subscribers.forEach((s) => { var _a; return (_a = s.setConnected) === null || _a === void 0 ? void 0 : _a.call(s, false); });
                    this.subscribers.forEach((s) => { var _a; return (_a = s.onClose) === null || _a === void 0 ? void 0 : _a.call(s, ev); });
                    if (this.subscribers.size > 0 && !this.reconnectTimer) {
                        this.reconnectTimer = setTimeout(() => {
                            this.reconnectTimer = null;
                            this.connect();
                        }, 3000);
                    }
                };
            }
            catch (e) {
                this.connecting = false;
                this.subscribers.forEach((s) => { var _a; return (_a = s.onError) === null || _a === void 0 ? void 0 : _a.call(s, 'connection_failed'); });
            }
        });
    }
    send(msg) {
        const messageWithId = Object.assign(Object.assign({}, msg), { correlation_id: uuidv4() });
        const ws = this.socket;
        if ((ws === null || ws === void 0 ? void 0 : ws.readyState) === WebSocket.OPEN) {
            ws.send(JSON.stringify(messageWithId));
            return true;
        }
        return false;
    }
    onSocketChange(listener) {
        this.socketListeners.add(listener);
        listener(this.socket);
        return () => {
            this.socketListeners.delete(listener);
        };
    }
    emitSocketChange(ws) {
        this.socketListeners.forEach((cb) => {
            try {
                cb(ws);
            }
            catch (_a) { }
        });
    }
}
LivequeueConnection.instances = new Map();
export const useLivequeueWs = ({ url, onMessage, onOpen, onClose, onError, }) => {
    const { t } = useTranslation();
    const socketRef = useRef(null);
    const isMountedRef = useRef(true);
    const [connected, setConnected] = useState(false);
    const handlersRef = useRef({ onMessage, onOpen, onClose, onError });
    useEffect(() => {
        handlersRef.current = { onMessage, onOpen, onClose, onError };
    }, [onMessage, onOpen, onClose, onError]);
    const safeSetConnected = useCallback((val) => {
        if (isMountedRef.current)
            setConnected(val);
    }, []);
    const connectionRef = useRef(null);
    if (!connectionRef.current) {
        connectionRef.current = LivequeueConnection.get(url);
    }
    const sendMessage = useCallback((msg) => {
        var _a;
        const ok = (_a = connectionRef.current) === null || _a === void 0 ? void 0 : _a.send(msg);
        if (!ok) {
            showAlert.error(t('ws.error.not_opened'));
        }
    }, [t]);
    const subscriberRef = useRef(null);
    useEffect(() => {
        isMountedRef.current = true;
        const connection = connectionRef.current;
        const offSocket = connection.onSocketChange((ws) => {
            socketRef.current = ws;
        });
        const subscriber = {
            setConnected: (val) => safeSetConnected(val),
            onOpen: () => { var _a, _b; return (_b = (_a = handlersRef.current).onOpen) === null || _b === void 0 ? void 0 : _b.call(_a); },
            onClose: (ev) => { var _a, _b; return (_b = (_a = handlersRef.current).onClose) === null || _b === void 0 ? void 0 : _b.call(_a, ev); },
            onError: (err) => { var _a, _b; return (_b = (_a = handlersRef.current).onError) === null || _b === void 0 ? void 0 : _b.call(_a, err); },
            onMessage: (msg) => {
                var _a, _b;
                try {
                    (_b = (_a = handlersRef.current).onMessage) === null || _b === void 0 ? void 0 : _b.call(_a, msg);
                }
                catch (_c) { }
            },
        };
        subscriberRef.current = subscriber;
        connection.addSubscriber(subscriber);
        return () => {
            isMountedRef.current = false;
            offSocket();
            if (subscriberRef.current) {
                connection.removeSubscriber(subscriberRef.current);
                subscriberRef.current = null;
            }
        };
    }, [safeSetConnected]);
    const disconnect = useCallback(() => {
        const connection = connectionRef.current;
        if (connection && subscriberRef.current) {
            connection.removeSubscriber(subscriberRef.current);
            subscriberRef.current = null;
        }
    }, []);
    return { connected, sendMessage, socketRef, disconnect };
};
